import { Injectable } from '@angular/core';
import {
  Style,
  SkiStyle,
  ClassicStyle,
  TwilightStyle,
  ClassicGraniteStyle,
  ClassicChromeStyle,
  ClassicBlackStyle,
  TwilightNeonStyle,
  TwilightRedStyle,
  BlueprintDeepStyle,
  BlueprintStyle,
  BlueprintPurpleStyle,
  BlueprintRoyalBlueStyle,
  BlueprintCoralStyle,
  BlueprintWhiteStyle,
  BlueprintSurfblueStyle,
  BlueprintOliveStyle,
  BlueprintYellowStyle,
  BlueprintDarkStyle,
  BlueprintPinkStyle,
  BlueprintCharcoalStyle,
  BlueprintBlackStyle,
  BlueprintPurpleDarkStyle,
  BlueprintCactusStyle,
  BlueprintHazelnutIceCreamStyle,
  BlueprintGreenStyle,
  BlueprintChocolateIceCreamStyle,
  BlueprintGrapefruitStyle,
  BlueprintOrangeStyle,
  BlueprintRedStyle,
  StyleColors
} from '../models/Style';
import { Finish, FinishId } from '../models/Finish';
import { ClassicGradient, NoneGradient } from '../models/Gradient';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Creates a finish based on the specified style colors
 *
 */
const createFinish = (styleId: string, colors: StyleColors) => (id: FinishId): Finish => {
  switch (id) {
    case 'classic':
      return {
        id: 'classic',
        name: 'Classic',
        description: 'FINISH_DESC_THREE_LINES'
      };

    case 'simple':
      return {
        id: 'simple',
        name: 'Simple',
        description: 'FINISH_DESC_SINGLE_LINE'
      };

    case 'no-text':
      return {
        id: 'no-text',
        name: 'No text',
        description: 'FINISH_DESC_NO_TEXT_BORDERS'
      };

    case 'canvas-only':
      return {
        id: 'canvas-only',
        name: 'Clean',
        description: 'FINISH_DESC_NO_TEXT_NO_BORDERS'
      };

    default:
      throw new Error(`FinishId: ${id} not supported yet!`);
  }
};

const createStyles = (sanitizer: DomSanitizer): Style[] => {
  // colors
  const classicColors: StyleColors = {
    primary: '#fff',
    secondary: '#000'
  };

  const twilightColors: StyleColors = {
    primary: '#000',
    secondary: '#bb7844'
  };

  const classicGraniteColors: StyleColors = {
    primary: '#d5d5d5',
    secondary: '#343331'
  };

  const classicChromeColors: StyleColors = {
    primary: '#ffffff',
    secondary: '#2c404b'
  };

  const classicBlackColors: StyleColors = {
    primary: '#000',
    secondary: '#ffffff'
  };

  const twilightNeonColors: StyleColors = {
    primary: '#000',
    secondary: '#2145f0'
  };

  const twilightRedColors: StyleColors = {
    primary: '#000',
    secondary: '#be0303'
  };

  const blueprintColors: StyleColors = {
    primary: '#435cb1',
    secondary: '#ffffff'
  };

  const blueprintDeepColors: StyleColors = {
    primary: '#001484',
    secondary: '#ffffff'
  };

  const blueprintPurpleColors: StyleColors = {
    primary: '#800080',
    secondary: '#ffffff'
  };
  const blueprintRoyalBlueColors: StyleColors = {
    primary: '#49638a',
    secondary: '#ffffff'
  };
  const blueprintCoralColors: StyleColors = {
    primary: '#faac6c',
    secondary: '#ffffff'
  };
  const blueprintWhiteColors: StyleColors = {
    primary: '#ffffff',
    secondary: '#000000'
  };
  const blueprintSurfblueColors: StyleColors = {
    primary: '#3d89c1',
    secondary: '#ffffff'
  };
  const blueprintOliveColors: StyleColors = {
    primary: '#8a977e',
    secondary: '#ffffff'
  };
  const blueprintYellowColors: StyleColors = {
    primary: '#f4c20d',
    secondary: '#ffffff'
  };
  const blueprintDarkColors: StyleColors = {
    primary: '#2a313f',
    secondary: '#ffffff'
  };
  const blueprintPinkColors: StyleColors = {
    primary: '#ffc0cb',
    secondary: '#ffffff'
  };
  const blueprintCharcoalColors: StyleColors = {
    primary: '#242424',
    secondary: '#ffffff'
  };
  const blueprintBlackColors: StyleColors = {
    primary: '#000000',
    secondary: '#ffffff'
  };
  const blueprintPurpleDarkColors: StyleColors = {
    primary: '#320032',
    secondary: '#ffffff'
  };
  const blueprintCactusColors: StyleColors = {
    primary: '#708d8d',
    secondary: '#ffffff'
  };
  const blueprintHazelnutIceCreamColors: StyleColors = {
    primary: '#fefbea',
    secondary: '#c6a381'
  };
  const blueprintGreenColors: StyleColors = {
    primary: '#143725',
    secondary: '#ffffff'
  };
  const blueprintChocolateIceCreamColors: StyleColors = {
    primary: '#7d5745',
    secondary: '#30160f'
  };
  const blueprintGrapefruitColors: StyleColors = {
    primary: '#fec3b2',
    secondary: '#ffffff'
  };
  const blueprintOrangeColors: StyleColors = {
    primary: '#e55300',
    secondary: '#ffffff'
  };
  const blueprintRedColors: StyleColors = {
    primary: '#840000',
    secondary: '#ffffff'
  };

  // styles
  const classic: ClassicStyle = {
    id: 'https://tiles.mapify-studio.com/styles/classic.json',
    name: 'classic',
    colors: classicColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('classic', classicColors)),
    gradient: new ClassicGradient()
  };

  const twilight: TwilightStyle = {
    id: 'https://tiles.mapify-studio.com/styles/twilight.json',
    name: 'twilight',
    colors: twilightColors,
    finishes: ['classic', 'simple', 'canvas-only'].map(createFinish('twilight', twilightColors)),
    gradient: new NoneGradient()
  };

  const classicGranite: ClassicGraniteStyle = {
    id: 'https://tiles.mapify-studio.com/styles/classic-granite.json',
    name: 'classic-granite',
    colors: classicGraniteColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('classic-granite', classicGraniteColors)),
    gradient: new ClassicGradient([52, 51, 49])
  };

  const classicChrome: ClassicChromeStyle = {
    id: 'https://tiles.mapify-studio.com/styles/classic-chrome.json',
    name: 'classic-chrome',
    colors: classicChromeColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('classic-chrome', classicChromeColors)),
    gradient: new ClassicGradient([44, 64, 75])
  };

  const classicBlack: ClassicBlackStyle = {
    id: 'https://tiles.mapify-studio.com/styles/classic-black.json',
    name: 'classic-black',
    colors: classicBlackColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('classic-black', classicBlackColors)),
    gradient: new ClassicGradient([0, 0, 0])
  };

  const twilightNeon: TwilightNeonStyle = {
    id: 'https://tiles.mapify-studio.com/styles/twilight-neon.json',
    name: 'twilight-neon',
    colors: twilightNeonColors,
    finishes: ['classic', 'simple', 'canvas-only'].map(createFinish('twilight-neon', twilightNeonColors)),
    gradient: new ClassicGradient([0, 0, 0])
  };

  const twilightRed: TwilightRedStyle = {
    id: 'https://tiles.mapify-studio.com/styles/twilight-red.json',
    name: 'twilight-red',
    colors: twilightRedColors,
    finishes: ['classic', 'simple', 'canvas-only'].map(createFinish('twilight-red', twilightRedColors)),
    gradient: new ClassicGradient([0, 0, 0])
  };

  const blueprintDeep: BlueprintDeepStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-deep-no-labels.json',
    name: 'blueprint-deep-no-labels',
    colors: blueprintDeepColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-deep', blueprintDeepColors)),
    gradient: new ClassicGradient([0, 20, 132])
  };

  const blueprint: BlueprintStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-no-labels.json',
    name: 'blueprint-no-labels',
    colors: blueprintColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint', blueprintColors)),
    gradient: new ClassicGradient([67, 92, 177])
  };

  const blueprintPurple: BlueprintPurpleStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-purple-no-labels.json',
    name: 'blueprint-purple-no-labels',
    colors: blueprintPurpleColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-purple', blueprintPurpleColors)),
    gradient: new ClassicGradient([128, 0, 128])
  };
  const blueprintRoyalBlue: BlueprintRoyalBlueStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-royal-blue-no-labels.json',
    name: 'blueprint-royal-blue-no-labels',
    colors: blueprintRoyalBlueColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-royal-blue', blueprintRoyalBlueColors)),
    gradient: new ClassicGradient([73, 99, 138])
  };
  const blueprintCoral: BlueprintCoralStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-coral-no-labels.json',
    name: 'blueprint-coral-no-labels',
    colors: blueprintCoralColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-coral', blueprintCoralColors)),
    gradient: new ClassicGradient([250, 172, 108])
  };
  const blueprintWhite: BlueprintWhiteStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-white-no-labels.json',
    name: 'blueprint-white-no-labels',
    colors: blueprintWhiteColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-white', blueprintWhiteColors)),
    gradient: new ClassicGradient([255, 255, 255])
  };
  const blueprintSurfblue: BlueprintSurfblueStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-surfblue-no-labels.json',
    name: 'blueprint-surfblue-no-labels',
    colors: blueprintSurfblueColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-surfblue', blueprintSurfblueColors)),
    gradient: new ClassicGradient([61, 137, 193])
  };
  const blueprintOlive: BlueprintOliveStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-olive-no-labels.json',
    name: 'blueprint-olive-no-labels',
    colors: blueprintOliveColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-olive', blueprintOliveColors)),
    gradient: new ClassicGradient([138, 151, 126])
  };
  const blueprintYellow: BlueprintYellowStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-yellow-no-labels.json',
    name: 'blueprint-yellow-no-labels',
    colors: blueprintYellowColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-yellow', blueprintYellowColors)),
    gradient: new ClassicGradient([244, 194, 13])
  };
  const blueprintDark: BlueprintDarkStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-dark-no-labels.json',
    name: 'blueprint-dark-no-labels',
    colors: blueprintDarkColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-dark', blueprintDarkColors)),
    gradient: new ClassicGradient([42, 49, 63])
  };
  const blueprintPink: BlueprintPinkStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-pink-no-labels.json',
    name: 'blueprint-pink-no-labels',
    colors: blueprintPinkColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-pink', blueprintPinkColors)),
    gradient: new ClassicGradient([255, 192, 203])
  };
  const blueprintCharcoal: BlueprintCharcoalStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-charcoal-no-labels.json',
    name: 'blueprint-charcoal-no-labels',
    colors: blueprintCharcoalColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-charcoal', blueprintCharcoalColors)),
    gradient: new ClassicGradient([36, 36, 36])
  };
  const blueprintBlack: BlueprintBlackStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-black-no-labels.json',
    name: 'blueprint-black-no-labels',
    colors: blueprintBlackColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-black', blueprintBlackColors)),
    gradient: new ClassicGradient([0, 0, 0])
  };
  const blueprintPurpleDark: BlueprintPurpleDarkStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-purple-dark-no-labels.json',
    name: 'blueprint-purple-dark-no-labels',
    colors: blueprintPurpleDarkColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(
      createFinish('blueprint-purple-dark', blueprintPurpleDarkColors)
    ),
    gradient: new ClassicGradient([50, 0, 50])
  };
  const blueprintCactus: BlueprintCactusStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-cactus-no-labels.json',
    name: 'blueprint-cactus-no-labels',
    colors: blueprintCactusColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-cactus', blueprintCactusColors)),
    gradient: new ClassicGradient([112, 141, 141])
  };
  const blueprintHazelnutIceCream: BlueprintHazelnutIceCreamStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-hazelnut-ice-cream-no-labels.json',
    name: 'blueprint-hazelnut-ice-cream-no-labels',
    colors: blueprintHazelnutIceCreamColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(
      createFinish('blueprint-hazelnut-ice-cream', blueprintHazelnutIceCreamColors)
    ),
    gradient: new ClassicGradient([254, 251, 234])
  };
  const blueprintGreen: BlueprintGreenStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-green-no-labels.json',
    name: 'blueprint-green-no-labels',
    colors: blueprintGreenColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-green', blueprintGreenColors)),
    gradient: new ClassicGradient([20, 55, 37])
  };
  const blueprintChocolateIceCream: BlueprintChocolateIceCreamStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-chocolate-ice-cream-no-labels.json',
    name: 'blueprint-chocolate-ice-cream-no-labels',
    colors: blueprintChocolateIceCreamColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(
      createFinish('blueprint-chocolate-ice-cream', blueprintChocolateIceCreamColors)
    ),
    gradient: new ClassicGradient([125, 87, 69])
  };
  const blueprintGrapefruit: BlueprintGrapefruitStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-grapefruit-no-labels.json',
    name: 'blueprint-grapefruit-no-labels',
    colors: blueprintGrapefruitColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(
      createFinish('blueprint-grapefruit', blueprintGrapefruitColors)
    ),
    gradient: new ClassicGradient([254, 195, 178])
  };
  const blueprintOrange: BlueprintOrangeStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-orange-no-labels.json',
    name: 'blueprint-orange-no-labels',
    colors: blueprintOrangeColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-orange', blueprintOrangeColors)),
    gradient: new ClassicGradient([229, 83, 0])
  };
  const blueprintRed: BlueprintRedStyle = {
    id: 'https://tiles.mapify-studio.com/styles/blueprint-red-no-labels.json',
    name: 'blueprint-red-no-labels',
    colors: blueprintRedColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('blueprint-red', blueprintRedColors)),
    gradient: new ClassicGradient([132, 0, 0])
  };
  const ski: SkiStyle = {
    id: 'https://tiles.mapify-studio.com/styles/ski.json',
    name: 'ski',
    colors: classicColors,
    finishes: ['classic', 'no-text', 'canvas-only'].map(createFinish('classic', classicColors)),
    gradient: new ClassicGradient()
  };

  return [
    ski,
    classic,
    twilight,
    classicGranite,
    classicChrome,
    classicBlack,
    twilightNeon,
    twilightRed,
    blueprint,
    blueprintDeep,
    blueprintGreen,
    blueprintRed,
    //blueprintOrange,
    //blueprintPurple,
    //blueprintRoyalBlue,
    //blueprintCoral,
    //blueprintWhite,
    //blueprintSurfblue,
    //blueprintOlive,
    blueprintYellow,
    //blueprintDark,
    blueprintPink,
    //blueprintCharcoal,
    //blueprintBlack,
    //blueprintPurpleDark,
    //blueprintCactus,
    blueprintHazelnutIceCream,
    blueprintChocolateIceCream
    //blueprintGrapefruit,
  ];
};

@Injectable()
export class StyleService {
  private styles: Style[];

  constructor(private sanitizer: DomSanitizer) {
    this.styles = createStyles(sanitizer);
  }

  list(): Style[] {
    return this.styles;
  }
}



// WEBPACK FOOTER //
// /vercel/path0/src/app/services/style.service.ts